import { MutationFunctionOptions, useMutation } from '@apollo/client';

import {
  CreateInternationalTransferInput,
  Exact,
  InternationalTransferPayout,
  InternationalTransferProviderErrorResponse,
} from '__generated__/GQL';

import {
  CreateInternationalTransferDocument,
  CreateInternationalTransferMutation,
} from './graphql/createInternationalTransfer.gql';

const isResponseSuccess = (
  obj: CreateInternationalTransferMutation['createInternationalTransfer'],
): obj is InternationalTransferPayout => {
  if (!obj) {
    return false;
  }

  return 'internationalTransferPayoutId' in obj;
};

const isResponseError = (
  obj: CreateInternationalTransferMutation['createInternationalTransfer'],
): obj is InternationalTransferProviderErrorResponse => {
  if (!obj) {
    return false;
  }

  return !('internationalTransferPayoutId' in obj);
};

const getTransferAndErrorsFromData = (
  data: CreateInternationalTransferMutation | null | undefined,
) => {
  if (!data) {
    return {
      errors: null,
      transfer: null,
    };
  }

  const quote = isResponseSuccess(data.createInternationalTransfer)
    ? data.createInternationalTransfer
    : null;

  const errors = isResponseError(data.createInternationalTransfer)
    ? data.createInternationalTransfer.errors
    : null;

  return {
    errors,
    quote,
  };
};

export const useCreateInternationalTransfer = () => {
  const [createTransfer, { loading: isCreatingTransfer }] = useMutation(
    CreateInternationalTransferDocument,
  );

  const createInternationalTransfer = async (
    options: MutationFunctionOptions<
      CreateInternationalTransferMutation,
      Exact<{
        input: CreateInternationalTransferInput;
      }>
    >,
  ) => {
    const { data: mutateData } = await createTransfer(options);

    return {
      isCreatingTransfer: false,
      ...getTransferAndErrorsFromData(mutateData),
    };
  };

  return {
    createInternationalTransfer,
    isCreatingTransfer,
  };
};

export type UseInternationalTransferReturnType = ReturnType<
  typeof useCreateInternationalTransfer
>;
